/**
 * Frontend network config — testnet defaults in code; mainnet from frontend/.env
 * Set REACT_APP_WORKLOOB_NETWORK=mainnet + contract addresses for production.
 */

const BASE_SEPOLIA = {
  chainId: 84532,
  chainName: 'Base Sepolia',
  nativeCurrency: { name: 'ETH', symbol: 'ETH', decimals: 18 },
  rpcUrls: ['https://sepolia.base.org'],
  blockExplorerUrls: ['https://sepolia.basescan.org'],
  usdc: '0xC22F423E9E13292e798842755653De4B12eE5C72',
  contracts: {
    worklobJob: '0x7360Bae982fA01DfcB1154Bd16F485B2855526d3',
    worklobDao: '0x9a7A824F9B46C3Da0A08e997D3ce54F4561Fea6e',
    worklobStaking: '0x2Cc0621d0A6F12F629Aa253bd1B8a6e95A758f32',
    lobToken: '0x7da6fA4bdb31f2DF674B9C555F68d06933770cAa',
  },
  deployerWallet: '0x295bD6C0d72336Ce54338680696a08102F5CCc7D',
};

const BASE_MAINNET_META = {
  chainId: 8453,
  chainName: 'Base Mainnet',
  nativeCurrency: { name: 'ETH', symbol: 'ETH', decimals: 18 },
  rpcUrls: ['https://mainnet.base.org', 'https://base-rpc.publicnode.com'],
  blockExplorerUrls: ['https://basescan.org'],
  usdc: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913',
};

function pickEnv(...keys) {
  for (const key of keys) {
    const v = process.env[key];
    if (v != null && String(v).trim() !== '') return String(v).trim();
  }
  return '';
}

export function isMainnetMode() {
  const flag = pickEnv('REACT_APP_WORKLOOB_NETWORK').toLowerCase();
  if (flag === 'mainnet') return true;
  if (flag === 'testnet' || flag === 'sepolia') return false;
  return false;
}

export function getWorkloobNetwork() {
  const mainnet = isMainnetMode();

  if (!mainnet) {
    const rpcOverride = pickEnv('REACT_APP_BASE_RPC_URL');
    const rpcUrls = rpcOverride ? [rpcOverride, ...BASE_SEPOLIA.rpcUrls] : [...BASE_SEPOLIA.rpcUrls];
    return {
      network: 'testnet',
      isMainnet: false,
      chainId: BASE_SEPOLIA.chainId,
      chainName: BASE_SEPOLIA.chainName,
      nativeCurrency: BASE_SEPOLIA.nativeCurrency,
      rpcUrls,
      rpcUrl: rpcUrls[0],
      blockExplorerUrls: BASE_SEPOLIA.blockExplorerUrls,
      usdc: pickEnv('REACT_APP_USDC_TOKEN_ADDRESS') || BASE_SEPOLIA.usdc,
      worklobJobAddress:
        pickEnv('REACT_APP_WORKLOB_JOB_CONTRACT_ADDRESS') || BASE_SEPOLIA.contracts.worklobJob,
      worklobDaoAddress:
        pickEnv('REACT_APP_WORKLOB_DAO_CONTRACT_ADDRESS') || BASE_SEPOLIA.contracts.worklobDao,
      worklobStakingAddress:
        pickEnv('REACT_APP_WORKLOB_STAKING_CONTRACT_ADDRESS') || BASE_SEPOLIA.contracts.worklobStaking,
      lobTokenAddress:
        pickEnv('REACT_APP_LOB_TOKEN_ADDRESS') || BASE_SEPOLIA.contracts.lobToken,
      deployerWallet: (
        pickEnv('REACT_APP_DEPLOYER_WALLET_ADDRESS') || BASE_SEPOLIA.deployerWallet
      ).toLowerCase(),
    };
  }

  const rpcUrl = pickEnv('REACT_APP_BASE_RPC_URL') || BASE_MAINNET_META.rpcUrls[0];
  const rpcUrls = [rpcUrl, ...BASE_MAINNET_META.rpcUrls.filter((u) => u !== rpcUrl)];

  return {
    network: 'mainnet',
    isMainnet: true,
    chainId: BASE_MAINNET_META.chainId,
    chainName: BASE_MAINNET_META.chainName,
    nativeCurrency: BASE_MAINNET_META.nativeCurrency,
    rpcUrls,
    rpcUrl,
    blockExplorerUrls: BASE_MAINNET_META.blockExplorerUrls,
    usdc: pickEnv('REACT_APP_USDC_TOKEN_ADDRESS') || BASE_MAINNET_META.usdc,
    worklobJobAddress: pickEnv('REACT_APP_WORKLOB_JOB_CONTRACT_ADDRESS'),
    worklobDaoAddress: pickEnv('REACT_APP_WORKLOB_DAO_CONTRACT_ADDRESS'),
    worklobStakingAddress: pickEnv('REACT_APP_WORKLOB_STAKING_CONTRACT_ADDRESS'),
    lobTokenAddress: pickEnv('REACT_APP_LOB_TOKEN_ADDRESS'),
    deployerWallet: (
      pickEnv('REACT_APP_DEPLOYER_WALLET_ADDRESS') || BASE_SEPOLIA.deployerWallet
    ).toLowerCase(),
  };
}

export function toWalletChainParams(net = getWorkloobNetwork()) {
  return {
    chainId: net.chainId,
    chainName: net.chainName,
    nativeCurrency: net.nativeCurrency,
    rpcUrls: net.rpcUrls,
    blockExplorerUrls: net.blockExplorerUrls,
  };
}

/** Active network (computed once at module load for CRA env inlining). */
export const WORKLOOB_NET = getWorkloobNetwork();
